import React, { useState, useEffect } from "react";
import {
  Save,
  X,
  Pencil,
  Calendar,
  Clock,
  Hospital,
  BarChart2,
  FileText,
  Plus,
  AirVent,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
} from "../../../shared/utils/permissoesCategoria";

interface PosOperatorioProps {
  paciente: Paciente;
  onSalvarTx?: (dados: any) => void;
}

export default function PosOperatorio({
  paciente,
  onSalvarTx,
}: PosOperatorioProps) {
  const { atualizarPaciente } = usePacientes();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): dados do transplante sao campos CLINICOS do doc
  // pacientes - edicao restrita a equipe clinica (espelho das
  // firestore.rules). Nome legado: hoje significa "somente leitura"
  // (vale p/ convidado E administrativo).
  const isConvidado =
    !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dados, setDados] = useState<{
    dataExtubacao?: string;
    horaExtubacao?: string;
    diasUTI?: string;
    dataAlta?: string;
    observacoes?: string;
    editadoEm?: string;
  }>({});

  // ✅ CARREGAR dados
  useEffect(() => {
    const pos = paciente.dadosTransplante?.posOperatorio;
    if (pos && Object.values(pos).some((v) => v && String(v).trim() !== "")) {
      setDados(pos);
    } else {
      setDados({});
    }
  }, [paciente.id, paciente.dadosTransplante?.posOperatorio]);

  const temDados = Object.values(dados).some((v) => v && v.trim() !== "");

  const handleSalvar = () => {
    const temDadosPreenchidos = Object.entries(dados).some(
      ([k, v]) => k !== "editadoEm" && v && String(v).trim() !== ""
    );
    if (!temDadosPreenchidos) {
      alert("Preencha pelo menos um campo antes de salvar.");
      return;
    }
    // 📅 B296 — datas pós-operatórias não podem ser futuras (permite hoje/retroativo).
    const camposData: Array<[string, any]> = [
      ["Data da extubação", (dados as any).dataExtubacao],
      ["Data de alta", (dados as any).dataAlta],
    ];
    const futuro = camposData.find(([, v]) => ehDataFutura(v));
    if (futuro) {
      alert(msgDataFutura(futuro[0]));
      return;
    }
    const dadosComTimestamp = { ...dados, editadoEm: new Date().toISOString() };
    if (onSalvarTx) {
      onSalvarTx(dadosComTimestamp);
    } else {
      atualizarPaciente(paciente.id, {
        dadosTransplante: {
          ...paciente.dadosTransplante,
          posOperatorio: dadosComTimestamp,
        },
      });
    }
    setModoEdicao(false);
  };

  // Janela de 24h para edição
  const editadoEm = paciente.dadosTransplante?.posOperatorio?.editadoEm;
  const horasDecorridas = editadoEm
    ? (new Date().getTime() - new Date(editadoEm).getTime()) / (1000 * 60 * 60)
    : 0;
  const podeEditar = !temDados || horasDecorridas <= 24;

  // ⏱️ CALCULAR dias de internação
  const calcularDiasInternacao = (): string => {
    if (!paciente.inscricaoTx?.dataTransplante || !dados.dataAlta) return "-";

    const inicio = new Date(paciente.inscricaoTx.dataTransplante);
    const fim = new Date(dados.dataAlta);

    const diffTime = fim.getTime() - inicio.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? `${diffDays} dias` : "-";
  };

  return (
    <div>
      {/* BOTÕES */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
          gap: "8px",
        }}
      >
        {!isConvidado && (
          <>
            {modoEdicao ? (
              <>
                <button
                  onClick={handleSalvar}
                  style={{
                    padding: "8px 16px",
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                    color: "white",
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Save size={14} style={{ marginRight: "6px" }} /> Salvar
                </button>
                <button
                  onClick={() => {
                    setModoEdicao(false);
                    if (paciente.dadosTransplante?.posOperatorio) {
                      setDados(paciente.dadosTransplante.posOperatorio);
                    }
                  }}
                  style={{
                    padding: "8px 16px",
                    background: theme.gray[300],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <X size={14} style={{ marginRight: "6px" }} /> Cancelar
                </button>
              </>
            ) : podeEditar ? (
              <button
                onClick={() => setModoEdicao(true)}
                style={{
                  padding: "8px 16px",
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Pencil size={14} style={{ marginRight: "6px" }} /> Editar
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* MODO VISUALIZAÇÃO */}
      {!modoEdicao && temDados ? (
        <div>
          {/* EXTUBAÇÃO */}
          {(dados.dataExtubacao || dados.horaExtubacao) && (
            <div
              style={{
                background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
                border: `2px solid ${theme.primary[200]}`,
                borderRadius: theme.borderRadius.lg,
                padding: "20px",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: theme.primary[700],
                  marginBottom: "16px",
                  fontFamily: theme.fonts.sans,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <AirVent size={14} style={{ marginRight: "6px" }} /> Extubação
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                }}
              >
                {dados.dataExtubacao && (
                  <div
                    style={{
                      background: "white",
                      padding: "16px",
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.primary[200]}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: theme.primary[600],
                        marginBottom: "6px",
                        fontFamily: theme.fonts.sans,
                        fontWeight: "600",
                      }}
                    >
                      <Calendar size={12} style={{ marginRight: "4px" }} /> DATA
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: theme.primary[700],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {new Date(
                        dados.dataExtubacao + "T00:00:00"
                      ).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                )}
                {dados.horaExtubacao && (
                  <div
                    style={{
                      background: "white",
                      padding: "16px",
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.primary[200]}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: theme.primary[600],
                        marginBottom: "6px",
                        fontFamily: theme.fonts.sans,
                        fontWeight: "600",
                      }}
                    >
                      <Clock size={12} style={{ marginRight: "4px" }} /> HORA
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: theme.primary[700],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {dados.horaExtubacao}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* INTERNAÇÃO */}
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
              border: `2px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: theme.primary[700],
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Hospital size={16} style={{ marginRight: "8px" }} /> Internação
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
              }}
            >
              {dados.diasUTI && (
                <div
                  style={{
                    background: "white",
                    padding: "16px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.primary[600],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                      fontWeight: "600",
                    }}
                  >
                    🛏️ DIAS DE UTI
                  </div>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.diasUTI}
                  </div>
                </div>
              )}
              {dados.dataAlta && (
                <div
                  style={{
                    background: "white",
                    padding: "16px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.primary[600],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                      fontWeight: "600",
                    }}
                  >
                    <Calendar size={12} style={{ marginRight: "4px" }} /> DATA
                    DE ALTA
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {new Date(dados.dataAlta + "T00:00:00").toLocaleDateString(
                      "pt-BR"
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  background: "#fef3c7",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #fbbf24",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#92400e",
                    marginBottom: "6px",
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  <BarChart2 size={12} style={{ marginRight: "4px" }} />{" "}
                  INTERNAÇÃO TOTAL
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#92400e",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calcularDiasInternacao()}
                </div>
              </div>
            </div>
          </div>

          {/* OBSERVAÇÕES */}
          {dados.observacoes && (
            <div
              style={{
                background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
                border: `2px solid ${theme.primary[200]}`,
                borderRadius: theme.borderRadius.lg,
                padding: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: theme.primary[700],
                  marginBottom: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <FileText size={14} style={{ marginRight: "6px" }} />{" "}
                Observações
              </h3>
              <div
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.primary[200]}`,
                  whiteSpace: "pre-wrap",
                  fontSize: "14px",
                  color: theme.gray[700],
                  fontFamily: theme.fonts.sans,
                  lineHeight: "1.6",
                }}
              >
                {dados.observacoes}
              </div>
            </div>
          )}
        </div>
      ) : !modoEdicao && !temDados ? (
        // ESTADO VAZIO
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            border: `2px dashed ${theme.gray[300]}`,
          }}
        >
          <Hospital
            size={48}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />
          <p
            style={{
              color: theme.gray[600],
              fontSize: "14px",
              fontFamily: theme.fonts.sans,
              marginBottom: "16px",
            }}
          >
            Dados do pós-operatório não cadastrados ainda
          </p>
          {!isConvidado && (
            <button
              onClick={() => setModoEdicao(true)}
              style={{
                padding: "10px 20px",
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Plus size={14} style={{ marginRight: "6px" }} /> Cadastrar Dados
            </button>
          )}
        </div>
      ) : (
        // MODO EDIÇÃO
        <div>
          {/* EXTUBAÇÃO */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <AirVent size={14} style={{ marginRight: "6px" }} /> Extubação
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Calendar size={14} style={{ marginRight: "6px" }} /> Data da
                  Extuba
                </label>
                <input
                  type="date"
                  value={dados.dataExtubacao || ""}
                  max={hojeISO()}
                  onChange={(e) =>
                    setDados({ ...dados, dataExtubacao: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Clock size={14} style={{ marginRight: "6px" }} /> Hora da
                  Extubação
                </label>
                <input
                  type="time"
                  value={dados.horaExtubacao || ""}
                  onChange={(e) =>
                    setDados({ ...dados, horaExtubacao: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </div>

          {/* INTERNAÇÃO */}
          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Hospital size={16} style={{ marginRight: "8px" }} /> Internação
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  🛏️ Dias de UTI
                </label>
                <input
                  type="number"
                  value={dados.diasUTI || ""}
                  onChange={(e) =>
                    setDados({ ...dados, diasUTI: e.target.value })
                  }
                  placeholder="Ex: 5"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Calendar size={14} style={{ marginRight: "6px" }} /> Data de
                  Alta
                </label>
                <input
                  type="date"
                  value={dados.dataAlta || ""}
                  max={hojeISO()}
                  onChange={(e) =>
                    setDados({ ...dados, dataAlta: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontFamily: theme.fonts.sans,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.gray[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <BarChart2 size={14} style={{ marginRight: "6px" }} /> Dias de
                  Internação Total (calculado)
                </label>
                <div
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "2px solid #fbbf24",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "16px",
                    fontFamily: theme.fonts.sans,
                    background: "#fef3c7",
                    fontWeight: "700",
                    color: "#92400e",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {calcularDiasInternacao()}
                </div>
              </div>
            </div>
          </div>

          {/* OBSERVAÇÕES */}
          <div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: theme.primary[700],
                marginBottom: "12px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <FileText size={14} style={{ marginRight: "6px" }} /> Observações
            </h3>
            <textarea
              value={dados.observacoes || ""}
              onChange={(e) =>
                setDados({ ...dados, observacoes: e.target.value })
              }
              rows={6}
              placeholder="Ex: Evolução pós-operatória sem intercorrências, paciente teve boa recuperação..."
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `2px solid ${theme.gray[300]}`,
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontFamily: theme.fonts.sans,
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
